import { createContext, useContext, useState, useEffect } from "react";
import type { ReactNode } from "react";

interface LocationData {
  city: string;
  state: string;
  latitude: number;
  longitude: number;
}

interface LocationContextType {
  location: LocationData | null;
  isLoading: boolean;
  error: string | null;
  requestLocation: () => Promise<void>;
}

const LocationContext = createContext<LocationContextType | undefined>(
  undefined
);

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error("useLocation must be used within a LocationProvider");
  }
  return context;
};

export const LocationProvider = ({ children }: { children: ReactNode }) => {
  const [location, setLocation] = useState<LocationData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reverseGeocode = async (
    latitude: number,
    longitude: number
  ): Promise<{ city: string; state: string }> => {
    try {
      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&language=pt-BR&key=${apiKey}`
      );
      const data = await response.json();

      if (data.results && data.results.length > 0) {
        let city = "Cidade não identificada";
        let state = "Estado não identificado";

        for (const component of data.results[0].address_components) {
          if (component.types.includes("administrative_area_level_2")) {
            city = component.long_name;
          }
          if (component.types.includes("administrative_area_level_1")) {
            state = component.long_name;
          }
        }

        return { city, state };
      }

      return { city: "Não identificado", state: "" };
    } catch (err) {
      console.error("Erro ao fazer reverse geocoding:", err);
      return { city: "Não identificado", state: "" };
    }
  };

  const requestLocation = async () => {
    setIsLoading(true);
    setError(null);

    if (!navigator.geolocation) {
      setError("Geolocalização não é suportada pelo seu navegador");
      setIsLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          const { city, state } = await reverseGeocode(latitude, longitude);

          const locationData: LocationData = {
            city,
            state,
            latitude,
            longitude,
          };

          setLocation(locationData);
          localStorage.setItem("userLocation", JSON.stringify(locationData));
          setIsLoading(false);
        } catch (err) {
          setError("Erro ao obter informações da localização");
          setIsLoading(false);
        }
      },
      (err) => {
        console.error("Erro ao obter localização:", err);
        setError("Localização não disponível");
        setLocation({
          city: "Não identificado",
          state: "",
          latitude: 0,
          longitude: 0,
        });
        setIsLoading(false);
      }
    );
  };

  // Carregar localização salva ao montar o componente
  useEffect(() => {
    const savedLocation = localStorage.getItem("userLocation");
    const token = localStorage.getItem("duas-vidas-token");

    if (savedLocation) {
      try {
        setLocation(JSON.parse(savedLocation));
      } catch (err) {
        console.error("Erro ao carregar localização salva:", err);
      }
    } else if (token) {
      // Se está autenticado mas não tem localização salva, solicitar
      requestLocation();
    }
  }, []);

  return (
    <LocationContext.Provider
      value={{ location, isLoading, error, requestLocation }}
    >
      {children}
    </LocationContext.Provider>
  );
};
